import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Wind, Rocket, ShieldCheck, Send, CheckCircle2, HelpCircle } from 'lucide-react';
import { UtmCapture } from '../components/UtmCapture';
import { getUtmParams } from '../utils/utmTracking';
export function WingFoilCampaignPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  useEffect(() => {
    document.title = "Wing Foil - בואו לעוף על המים | ביץ' קלאב מעגן";
  }, []);
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.phone.trim()) return;
    setIsSubmitting(true);
    // Capture UTM params
    const utmParams = getUtmParams();
    // Track Meta Pixel event for Lead
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'Lead', {
        content_name: 'Wing Foil Campaign',
        currency: 'ILS'
      });
    }
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      // Redirect to WhatsApp after successful submission
      const text = encodeURIComponent(`היי, השארתי פרטים בדף הקמפיין של הווינג פויל.\nשם: ${formData.fullName}\nטלפון: ${formData.phone}`);
      window.open(`https://wa.me/972548800260?text=${text}`, '_blank');
    }, 1000);
  };
  return <div className="min-h-screen bg-white" dir="rtl" data-id="element-2191">
      <UtmCapture data-id="element-2192" />

      {/* Minimal Header */}
      <header className="absolute top-0 left-0 right-0 z-50 p-6" data-id="element-2193">
        <div className="container mx-auto flex justify-center md:justify-start" data-id="element-2194">
          <img src="https://cdn.magicpatterns.com/uploads/bHRRxzcgoWuLZVsSMLwyGt/logo.png" alt="Beach Club Logo" className="h-12 md:h-16 brightness-0 invert" data-id="element-2195" />
        </div>
      </header>

      {/* Hero Section with Form */}
      <section className="relative min-h-[90vh] flex items-center pt-24 pb-12" data-id="element-2196">
        <div className="absolute inset-0 z-0" data-id="element-2197">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/co1SZT3RTYumCS8rU85uzt/IMG-20250502-WA0017.jpg" alt="Wing Foil" className="w-full h-full object-cover" style={{
          objectPosition: '60% 40%'
        }} data-id="element-2198" />
          <div className="absolute inset-0 bg-gradient-to-l from-black/90 via-black/60 to-black/20" data-id="element-2199"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-2200">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-2201">
            {/* Text Content */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-white" data-id="element-2202">
              <div className="inline-flex items-center gap-2 bg-cyan-500/20 border border-cyan-400/30 px-4 py-1.5 rounded-full mb-6 backdrop-blur-sm" data-id="element-2203">
                <Wind className="w-4 h-4 text-cyan-300" data-id="element-2204" />
                <span className="text-cyan-100 text-sm font-medium" data-id="element-2205">
                  הספורט הימי שמשגע את העולם
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight" data-id="element-2206">
                בואו לעוף <br data-id="element-2207" />
                <span className="text-cyan-400" data-id="element-2208">על המים</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-lg leading-relaxed font-light" data-id="element-2209">
                הצטרפו אלינו לביץ' קלאב מעגן ותגלו את תחושת הריחוף הממכרת של
                ה-Wing Foil. הדרכות למתחילים, ציוד חדיש וחוף מושלם.
              </p>

              <div className="space-y-4 hidden md:block" data-id="element-2210">
                <div className="flex items-center gap-3" data-id="element-2211">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-2212" />
                  <span className="text-lg" data-id="element-2213">ציוד קל ובטיחותי ללמידה מהירה</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-2214">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-2215" />
                  <span className="text-lg" data-id="element-2216">הדרכה מקצועית צמודה</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-2217">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-2218" />
                  <span className="text-lg" data-id="element-2219">תנאי רוח אידיאליים בכנרת</span>
                </div>
              </div>
            </motion.div>

            {/* Lead Form */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-3xl shadow-2xl max-w-md w-full mx-auto md:mx-0" data-id="element-2220">
              {isSubmitted ? <div className="text-center py-8" data-id="element-2221">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2222">
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" data-id="element-2223" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2224">
                    איזה כיף!
                  </h3>
                  <p className="text-gray-200" data-id="element-2225">
                    הפרטים התקבלו בהצלחה. נחזור אלייך בהקדם עם כל המידע.
                  </p>
                </div> : <>
                  <h3 className="text-2xl font-bold text-white mb-2 text-center" data-id="element-2226">
                    רוצה לשמוע עוד?
                  </h3>
                  <p className="text-gray-300 text-center mb-6 text-sm" data-id="element-2227">
                    השאירו פרטים ונחזור אליכם עם כל המידע על שיעורי ההיכרות שלנו
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2228">
                    <div data-id="element-2229">
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleFormChange} required placeholder="שם מלא" className="w-full bg-white/90 border-0 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-cyan-400 transition-all" data-id="element-2230" />
                    </div>
                    <div data-id="element-2231">
                      <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required dir="ltr" placeholder="טלפון" className="w-full bg-white/90 border-0 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-cyan-400 transition-all text-right" data-id="element-2232" />
                    </div>
                    <div data-id="element-2233">
                      <input type="email" name="email" value={formData.email} onChange={handleFormChange} dir="ltr" placeholder="אימייל (אופציונלי)" className="w-full bg-white/90 border-0 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-cyan-400 transition-all text-right" data-id="element-2234" />
                    </div>

                    <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold py-4 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 mt-2" data-id="element-2235">
                      {isSubmitting ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2236"></div> : <>
                          <Send className="w-5 h-5" data-id="element-2237" />
                          דברו איתי!
                        </>}
                    </button>
                  </form>
                </>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Benefits */}
      <section className="py-16 bg-gray-50" data-id="element-2238">
        <div className="container mx-auto px-4" data-id="element-2239">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-2240">
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center border border-gray-100" data-id="element-2241">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2242">
                <Rocket className="w-6 h-6 text-cyan-600" data-id="element-2243" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-2244">
                למידה מהירה
              </h3>
              <p className="text-gray-600" data-id="element-2245">
                עקומת למידה קלה יחסית לגלישת רוח או קייט. תוך מספר שיעורים כבר
                תרגישו את הריחוף.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center border border-gray-100" data-id="element-2246">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2247">
                <ShieldCheck className="w-6 h-6 text-cyan-600" data-id="element-2248" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-2249">
                בטיחות מעל הכל
              </h3>
              <p className="text-gray-600" data-id="element-2250">
                הכנף מוחזקת ביד ולא מחוברת לגוף, מה שהופך את הספורט לבטוח מאוד
                וקל לשליטה.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center border border-gray-100" data-id="element-2251">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2252">
                <Wind className="w-6 h-6 text-cyan-600" data-id="element-2253" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-2254">
                תנאים מושלמים
              </h3>
              <p className="text-gray-600" data-id="element-2255">
                רוחות הצהריים היציבות של הכנרת מספקות את התנאים האידיאליים ביותר
                ללמידה.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-id="element-2256">
        <div className="container mx-auto px-4" data-id="element-2257">
          <div className="max-w-3xl mx-auto" data-id="element-2258">
            <div className="text-center mb-12" data-id="element-2259">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-50 rounded-full mb-4" data-id="element-2260">
                <HelpCircle className="w-8 h-8 text-cyan-600" data-id="element-2261" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2262">
                כל מה שצריך לדעת
              </h2>
              <p className="text-xl text-gray-600" data-id="element-2263">
                שאלות ותשובות על הדרכות הווינג פויל שלנו
              </p>
            </div>

            <div className="space-y-6" data-id="element-2264">
              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2265">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2266">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2267">
                    1
                  </span>
                  איך בנויה ההדרכה?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2268">
                  ההדרכה שלנו משלבת חלק תיאורטי ומעשי, ובמהלכה לומדים את יסודות
                  הגלישה עם ווינג בצורה בטוחה וכיפית. משך כל הדרכה הוא כשעה
                  וחצי.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2269">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2270">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2271">
                    2
                  </span>
                  כמה מפגשים צריך כדי לגלוש לבד?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2272">
                  ברוב המקרים, כ־3 מפגשים מספיקים כדי להגיע לרמה שמאפשרת גלישה
                  עצמאית ובטוחה.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2273">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2274">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2275">
                    3
                  </span>
                  האם צריך להביא ציוד?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2276">
                  ממש לא! ההדרכה כוללת את כל הציוד הנדרש: ווינג, גלשן פויל,
                  חליפת הצלה וקסדה.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2277">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2278">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2279">
                    4
                  </span>
                  מה כן צריך להביא?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2280">
                  רק בגדים להחלפה ומגבת. אנחנו נדאג לכל השאר.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center bg-cyan-50 rounded-3xl p-8 border border-cyan-100" data-id="element-2281">
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2282">
                נשמע מעניין?
              </h3>
              <p className="text-lg text-gray-700 mb-6" data-id="element-2283">
                השאירו פרטים בטופס למעלה ונקבע לכם מועד שמתאים בדיוק לרוח!
              </p>
              <button onClick={() => window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-8 rounded-full transition-colors shadow-md" data-id="element-2284">
                חזרה להשארת פרטים ↑
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}